import { useContext, useState } from 'react'

import {
  ModalActions,
  ModalContent,
  ModalNavigation,
  SideModal,
} from '@twistopayments/design-system/components/atoms/modal'
import { ChevronDownIcon } from '@twistopayments/design-system/icons/inline'

import type { ConditionsSectionCustom, Navigation } from '@twisto/api-cms'

import { NestedLinksContext } from '../nested-links-provider'
import { PrimaryComponents } from '../primary/components/primary-components'
import { AuthenticationComponents } from '../secondary/components/authentication'
import { SecondaryComponents } from '../secondary/components/secondary-components'

import * as styles from './modal.styles'

type Props = {
  opened: boolean
  primaryComponents?: Navigation['primary']
  secondaryComponents?: Navigation['secondary']
  authenticationComponents?: Navigation['authentication']
  sections?: ConditionsSectionCustom[]
  onClose: () => void
}

const modalTitleId = 'id-modal-navigation'

const getPagesPath = (path: string) => {
  const cleanPath = path.replace(/^\/pages(?=\/)/, '')

  // Ensure trailing slash for Next.js trailingSlash: true config
  return cleanPath.endsWith('/') ? cleanPath : `${cleanPath}/`
}

export const Modal = ({
  opened,
  onClose,
  primaryComponents,
  secondaryComponents,
  sections,
  authenticationComponents,
}: Props) => {
  const { closeList, currentActiveListId } =
    useContext(NestedLinksContext) ?? {}

  const [openSectionId, setOpenSectionId] = useState<number | null>(null)

  const toggleSection = (id: number) => {
    setOpenSectionId((prev) => (prev === id ? null : id))
  }

  return (
    <SideModal
      aria-labelledby={modalTitleId}
      opened={opened}
      onClickOutside={onClose}
    >
      <ModalNavigation
        backButtonProps={
          currentActiveListId && closeList ? { onClick: closeList } : undefined
        }
        modalCloser={{
          onClick: onClose,
        }}
        title="Menu"
      />
      <nav>
        <ModalContent>
          <ul css={styles.primaryContainer}>
            <PrimaryComponents
              increaseFontSize={true}
              primaryNavigationContent={primaryComponents}
            />
          </ul>
        </ModalContent>
        {!currentActiveListId && (
          <ModalActions>
            <ul css={styles.secondaryContainer}>
              <SecondaryComponents
                increaseFontSize={true}
                secondaryNavigationContent={secondaryComponents}
                source="modal"
              />
              <AuthenticationComponents
                sort
                authenticationNavigationContent={authenticationComponents}
                source="modal"
              />
            </ul>
          </ModalActions>
        )}

        {sections && (
          <ModalContent>
            <ul css={styles.sectionsContainer}>
              {sections.map((section) => {
                const isSectionOpen = openSectionId === section.id
                const foldersWithPages = section.conditions_folders.filter(
                  (folder) => folder.conditions_pages.length > 0
                )

                return (
                  <li key={section.id} css={styles.sectionItem}>
                    <button
                      css={styles.sectionTitle}
                      onClick={() => toggleSection(section.id)}
                    >
                      {section.name}

                      <ChevronDownIcon size="24" />
                    </button>

                    <div
                      css={[
                        styles.sectionContent,
                        isSectionOpen && styles.openSectionContent,
                      ]}
                    >
                      <ul css={styles.pagesList}>
                        {section.conditions_pages.map((page) => (
                          <li key={page.id}>
                            <a href={getPagesPath(page.path)}>{page.name}</a>
                          </li>
                        ))}
                      </ul>

                      {foldersWithPages.map((folder) => (
                        <div key={folder.id}>
                          <p css={styles.folderName}>{folder.name}</p>
                          <ul css={styles.pagesList}>
                            {folder.conditions_pages.map((page) => (
                              <li key={page.id}>
                                <a href={getPagesPath(page.path)}>
                                  {page.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </li>
                )
              })}
            </ul>
          </ModalContent>
        )}
      </nav>
    </SideModal>
  )
}
